import { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const TRANSACTION_TYPES = [
  { value: 'payment', label: 'Payment', methods: ['etransfer_request_money', 'etransfer_standard'] },
  { value: 'payout', label: 'Payout', methods: ['etransfer_send_money'] },
  { value: 'refund', label: 'Refund', methods: ['etransfer_send_money'] },
  { value: 'chargeback', label: 'Chargeback', methods: ['etransfer_request_money'] }
];

const METHOD_LABELS: Record<string, string> = {
  etransfer_request_money: 'eTransfer Request Money',
  etransfer_standard: 'eTransfer Standard',
  etransfer_send_money: 'eTransfer Send Money'
};

import { Merchant, MerchantFee } from '../../types';

interface MerchantFeeFormProps {
  merchant: Merchant;
  onClose: () => void;
}

const STATUSES = [
  { value: 'approved', label: 'Approved' },
  { value: 'declined', label: 'Declined' },
  { value: 'returned', label: 'Returned' },
];

export function MerchantFeeForm({ merchant, onClose }: MerchantFeeFormProps) {
  const [fees, setFees] = useState<MerchantFee[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<string>(TRANSACTION_TYPES[0].value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFees();
  }, [merchant.id]);

  async function fetchFees() {
    try {
      const { data, error } = await supabase
        .from('merchant_fees')
        .select('*')
        .eq('merchant_id', merchant.id);

      if (error) throw error;
      setFees(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  }

  function getFee(type: string, method: string, status: string): MerchantFee {
    const existingFee = fees.find(
      (fee) => fee.type === type && fee.payment_method === method && fee.status === status
    );

    return existingFee || {
      id: crypto.randomUUID(),
      merchant_id: merchant.id,
      type: type as MerchantFee['type'],
      payment_method: method as MerchantFee['payment_method'],
      status: status as MerchantFee['status'],
      per_transaction_fee: 0,
      rate_fee_percentage: 0,
      max_fee_cap: 0,
    };
  }

  function handleFeeChange(
    type: string,
    method: string,
    status: string,
    field: keyof MerchantFee,
    value: string
  ) {
    const numericValue = value === '' ? 0 : parseFloat(value);
    const updatedFees = fees.map((fee) => {
      if (fee.type === type && fee.payment_method === method && fee.status === status) {
        return { ...fee, [field]: numericValue };
      }
      return fee;
    });

    const existingFeeIndex = updatedFees.findIndex(
      (fee) => fee.type === type && fee.payment_method === method && fee.status === status
    );

    if (existingFeeIndex === -1) {
      updatedFees.push({
        id: crypto.randomUUID(),
        merchant_id: merchant.id,
        type: type as MerchantFee['type'],
        payment_method: method as MerchantFee['payment_method'],
        status: status as MerchantFee['status'],
        per_transaction_fee: field === 'per_transaction_fee' ? numericValue : 0,
        rate_fee_percentage: field === 'rate_fee_percentage' ? numericValue : 0,
        max_fee_cap: field === 'max_fee_cap' ? numericValue : 0,
      });
    }

    setFees(updatedFees);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const feesToUpsert = fees.map((fee) => ({
        ...fee,
        merchant_id: merchant.id,
      }));

      const { error } = await supabase.from('merchant_fees').upsert(feesToUpsert, {
        onConflict: 'merchant_id,type,payment_method,status',
      });

      if (error) throw error;
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="sticky top-0 z-10 bg-white rounded-t-2xl border-b border-slate-100">
            <div className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Fee Structure</h2>
                <p className="mt-0.5 text-[13px] text-slate-500">{merchant.name}</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 flex gap-1 overflow-x-auto">
              {TRANSACTION_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setSelectedType(type.value)}
                  className={`px-4 py-2.5 text-[13px] font-medium rounded-t-lg border-b-2 transition-colors whitespace-nowrap ${
                    selectedType === type.value
                      ? 'border-violet-500 text-violet-600 bg-violet-50/50'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                {TRANSACTION_TYPES.map((type) => (
                  <div key={type.value} className={selectedType === type.value ? 'block' : 'hidden'}>
                    <div className="space-y-6">
                      {type.methods.map((method) => (
                        <div key={method} className="card">
                          <div className="px-5 py-3 border-b border-slate-100">
                            <h4 className="section-title">{METHOD_LABELS[method]}</h4>
                          </div>
                          <div className="p-5 space-y-4">
                            {STATUSES.map((status) => {
                              const fee = getFee(type.value, method, status.value);
                              return (
                                <div key={status.value} className="rounded-xl bg-slate-50 p-4">
                                  <h5 className="text-[13px] font-medium text-slate-700 mb-3">{status.label}</h5>
                                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                    <div>
                                      <label className="block text-[12px] font-medium text-slate-500 mb-1">Fixed Fee / Txn</label>
                                      <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                          <span className="text-slate-400 text-[13px]">$</span>
                                        </div>
                                        <input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          value={fee.per_transaction_fee || ''}
                                          onChange={(e) => handleFeeChange(type.value, method, status.value, 'per_transaction_fee', e.target.value)}
                                          className="w-full pl-7"
                                          placeholder="0.00"
                                        />
                                      </div>
                                    </div>
                                    <div>
                                      <label className="block text-[12px] font-medium text-slate-500 mb-1">Rate %</label>
                                      <div className="relative">
                                        <input
                                          type="number"
                                          step="0.001"
                                          min="0"
                                          max="100"
                                          value={fee.rate_fee_percentage || ''}
                                          onChange={(e) => handleFeeChange(type.value, method, status.value, 'rate_fee_percentage', e.target.value)}
                                          className="w-full pr-8"
                                          placeholder="0.000"
                                        />
                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                          <span className="text-slate-400 text-[13px]">%</span>
                                        </div>
                                      </div>
                                    </div>
                                    <div>
                                      <label className="block text-[12px] font-medium text-slate-500 mb-1">Max Cap</label>
                                      <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                          <span className="text-slate-400 text-[13px]">$</span>
                                        </div>
                                        <input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          value={fee.max_fee_cap || ''}
                                          onChange={(e) => handleFeeChange(type.value, method, status.value, 'max_fee_cap', e.target.value)}
                                          className="w-full pl-7"
                                          placeholder="0.00"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {error && (
                  <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-100">
                    <p className="text-[13px] text-red-600">{error}</p>
                  </div>
                )}

                <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                  <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                  <button type="submit" disabled={saving} className="btn-primary">
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
